import {
  actionColumn,
  BoundDateField,
  BoundNumberField,
  BoundSelectField,
  BoundTextField,
  Button,
  column,
  Css,
  GridDataRow,
  GridTable,
  GridTableApi,
  IconButton,
  simpleHeader,
  TextField,
  useComputed,
  useGridTableApi,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router";
import { Price } from "src/components";
import { PdfViewer } from "src/components/PdfViewer";
import {
  AssetInfoFragment,
  InputMaybe,
  LotAcqLotAcqSettlementStatementFragment,
  LotAcqLotAcqSettlementStatementLineItemFragment,
  LotAcqLotFragment,
  LotAcqSettlementStatementLineItemType,
  Maybe,
  SaveLotAcqSettlementStatementInput,
  useDeleteLotAcqSettlementStatementMutation,
  useLotAcqSettlementStatementDocumentsQuery,
  useProcessLotAcqSettlementStatementOcrMutation,
  useSaveLotAcqSettlementStatementMutation,
  useSendLotAcqSettlementStatementForBookingMutation,
} from "src/generated/graphql-types";
import { PageHeader } from "src/routes/layout/PageHeader";
import { DocumentIdParams } from "src/routes/routesDef";
import { createLotDetailsUrl } from "src/RouteUrls";
import { DateOnly } from "src/utils/dates";
import { queryResult } from "src/utils/queryResult";
import { fail, safeEntries } from "src/utils/utils";

export function LotAcqSettlementStatementEditor() {
  const { documentId } = useParams<DocumentIdParams>();

  const documentsQuery = useLotAcqSettlementStatementDocumentsQuery({
    variables: { documentIds: [documentId] },
    skip: !documentId,
  });

  return queryResult(documentsQuery, (data) => {
    const document = data.documents[0];
    const lot = document.parent as LotAcqLotFragment;
    const documentId = document.id;
    const lass = document.lotAcqSettlementStatement;
    // If it's new, it doesn't have a LASS to this document yet
    if (!lass) {
      return <LotAcqSettlementStatementForm documentId={documentId} lot={lot} assetInfo={document.asset} />;
    }
    // Otherwise it does
    return (
      <LotAcqSettlementStatementForm
        documentId={documentId}
        lot={lot}
        assetInfo={document.asset}
        lotAcqSettlementStatement={lass}
      />
    );
  });
}

type CreateLotAcqSettlementStatementEditorProps = {
  documentId?: string;
  lot: LotAcqLotFragment;
  assetInfo?: AssetInfoFragment;
  lotAcqSettlementStatement?: LotAcqLotAcqSettlementStatementFragment;
};

function LotAcqSettlementStatementForm({
  documentId,
  lot,
  assetInfo,
  lotAcqSettlementStatement,
}: CreateLotAcqSettlementStatementEditorProps) {
  const settlementStatement = lotAcqSettlementStatement;
  const [saveLotAcqSettlementStatement] = useSaveLotAcqSettlementStatementMutation();
  const [deleteLotAcqSettlementStatement] = useDeleteLotAcqSettlementStatementMutation();
  const [processLotAcqSettlementStatementOcr] = useProcessLotAcqSettlementStatementOcrMutation();
  const [sendLotAcqSettlementStatementForBooking] = useSendLotAcqSettlementStatementForBookingMutation();
  const history = useHistory();

  const isBooked = settlementStatement?.bookingEventSent;

  const tableApi = useGridTableApi<Row>();
  const formState = useFormState({
    config: formConfig,
    init: {
      input: settlementStatement
        ? mapSettlementStatementToForm(settlementStatement)
        : { documentId, lotId: lot.id, lineItems: [] },
    },
    readOnly: isBooked,
  });

  const saveLotAcqSettlementStatementFunction = useCallback(
    async (formState: ObjectState<LotAcqSettlementstatementFormInput>) => {
      const result = await saveLotAcqSettlementStatement({
        variables: {
          input: mapToInput(formState.value),
        },
      });

      // Update form state with the saved data from the backend
      const savedStatement = result.data?.saveLotAcqSettlementStatement?.lotAcqSettlementStatement;
      if (savedStatement) {
        formState.set(mapSettlementStatementToForm(savedStatement));
      }

      formState.commitChanges();
    },
    [saveLotAcqSettlementStatement],
  );

  function mapToInput(formStateValue: LotAcqSettlementstatementFormInput): SaveLotAcqSettlementStatementInput {
    const { documentId, lotId, ...others } = formStateValue;
    return {
      ...others,
      documentId,
      lotId,
      settlementDate: formStateValue.settlementDate ? new DateOnly(formStateValue.settlementDate) : undefined,
      lineItems: formStateValue.lineItems,
    };
  }

  const title = lotAcqSettlementStatement
    ? "Edit Settlement Statement for " + lot.address?.street1 + " (" + lot.id + ")"
    : "Create Settlement Statement for " + lot.address?.street1 + " (" + lot.id + ")";

  const bookingTooltip = settlementStatement?.canSendForBooking.allowed
    ? "Books the Settlement Statement to Accounting - cannot be undone"
    : settlementStatement?.canSendForBooking.disabledReasons.map((reason) => reason.message).join(", ");

  return (
    <>
      <PageHeader title={title} backButton={createLotDetailsUrl(lot.id)} />
      <TextField
        label="Investment Status"
        value={settlementStatement?.lot.lotInvestmentStatus.name}
        readOnly
        onChange={() => {}}
      />
      <TextField
        label="Legal Entity Acquiring the Lot"
        value={settlementStatement?.lot.legalEntityName || ""}
        readOnly
        onChange={() => {}}
      />
      <BoundTextField field={formState.buyerName} label="Buyer Name (from this document)" readOnly={isBooked} />
      <BoundDateField field={formState.settlementDate} label="Settlement Date" readOnly={isBooked} />
      Total Debits:
      <Price valueInCents={lotAcqSettlementStatement?.totalDebitsInCents} dropZero />
      Total Credits:
      <Price valueInCents={lotAcqSettlementStatement?.totalCreditsInCents} dropZero />
      <LineItemsTable formState={formState} tableApi={tableApi} />
      <div css={Css.df.fdr.gap1.$}>
        <Button
          variant="secondary"
          label="Add Line Item"
          onClick={() => {
            const newLineItems = [
              ...formState.lineItems.value,
              {
                id: undefined,
                amountInCents: 0,
                type: LotAcqSettlementStatementLineItemType.Other,
                notes: "",
              },
            ];
            formState.lineItems.set(newLineItems);
          }}
          disabled={isBooked}
        />
        <Button
          variant="secondary"
          label="Reprocess OCR"
          onClick={async () => {
            await processLotAcqSettlementStatementOcr({ variables: { input: { id: settlementStatement!.id } } });
            window.location.reload();
          }}
          disabled={isBooked}
        />
        <Button
          variant="primary"
          label="Save"
          onClick={async () => {
            await saveLotAcqSettlementStatementFunction(formState);
          }}
          disabled={isBooked}
        />
        <Button
          variant="secondary"
          label="Book To Accounting"
          icon="finances"
          onClick={async () => {
            // Send to booking
            await sendLotAcqSettlementStatementForBooking({ variables: { input: { id: settlementStatement!.id } } });
            // Then send them back to the lot
            history.push({ pathname: createLotDetailsUrl(settlementStatement!.lot.id) });
          }}
          disabled={!settlementStatement?.canSendForBooking.allowed}
          tooltip={bookingTooltip}
        />
        <IconButton
          icon="trash"
          tooltip="Delete Lot Acq Settlement Statement"
          onClick={async () => {
            await deleteLotAcqSettlementStatement({
              variables: { input: { id: settlementStatement?.id ?? fail("No settlement statement id") } },
            });
            history.push({ pathname: createLotDetailsUrl(settlementStatement!.lot.id) });
          }}
          disabled={isBooked}
        />
      </div>
      <PdfViewer
        hasHeader
        title={"Uploaded Settlement Statement PDF"}
        assets={[assetInfo]}
        pdfPageWidth={getDynamicPdfVierwerWidth()}
      />
    </>
  );
}

// When lineItems change (e.g., adding/removing rows), Observer detects the change
// and triggers a re-render. Without it, the GridTable won't update when items are added.
function LineItemsTable({
  formState,
  tableApi,
}: {
  formState: ObjectState<LotAcqSettlementstatementFormInput>;
  tableApi: GridTableApi<Row>;
}) {
  const rows = useComputed(() => formState.lineItems.rows, [formState.lineItems.rows]);
  return (
    <Observer>
      {() => {
        return <GridTable columns={createColumns(formState, tableApi)} rows={createRows(rows)} api={tableApi} />;
      }}
    </Observer>
  );
}

function createColumns(formState: ObjectState<LotAcqSettlementstatementFormInput>, tableApi: GridTableApi<Row>) {
  return [
    column<Row>({
      header: () => "Type",
      lineItem: (row) => (
        <BoundSelectField
          field={row.type}
          options={safeEntries(LotAcqSettlementStatementLineItemType).sort()}
          getOptionValue={([, code]) => code}
          getOptionLabel={([name]) => name}
        />
      ),
    }),
    column<Row>({
      header: () => "Amount",
      lineItem: (row) => ({
        value: () => row.amountInCents.value,
        content: () => <BoundNumberField type="cents" field={row.amountInCents} />,
        tooltip: "The amount of the line item",
      }),
      w: "200px",
    }),
    column<Row>({
      header: () => "Notes",
      lineItem: (row) => <BoundTextField field={row.notes} />,
      w: "600px",
    }),
    actionColumn<Row>({
      header: () => "Delete",
      lineItem: (_, { row }) => (
        <IconButton
          icon="trash"
          onClick={() => {
            const rows = tableApi.getVisibleRows("lineItem");
            const index = rows.findIndex((r) => r.id === row.id);
            formState.lineItems.set(formState.lineItems.rows.filter((_, idx) => idx !== index).map((r) => r.value));
            formState.commitChanges();
          }}
        />
      ),
    }),
  ];
}

function createRows(
  lineItems: ObjectState<LotAcqSettlementstatementFormInput>["lineItems"]["rows"],
): GridDataRow<Row>[] {
  const lineItemRows = lineItems.map((lineItemField, index) => ({
    kind: "lineItem" as const,
    id: lineItemField.id.value || `new-${index}`,
    data: lineItemField,
  })) as LineItemRow[];
  return [simpleHeader, ...lineItemRows];
}

export type LotAcqSettlementstatementFormInput = {
  id: InputMaybe<string>;
  lotId: InputMaybe<string>;
  documentId: InputMaybe<string>;
  buyerName: InputMaybe<string>;
  settlementDate: InputMaybe<Date>;
  lineItems: {
    id: InputMaybe<string>;
    type: InputMaybe<LotAcqSettlementStatementLineItemType>;
    amountInCents: InputMaybe<number>;
    notes: InputMaybe<string>;
  }[];
};

export const formConfig: ObjectConfig<LotAcqSettlementstatementFormInput> = {
  id: { type: "value" },
  lotId: { type: "value", rules: [required] },
  documentId: { type: "value", rules: [required] },
  buyerName: { type: "value", rules: [required] },
  settlementDate: { type: "value", rules: [required] },
  lineItems: {
    type: "list",
    config: {
      id: { type: "value" },
      type: { type: "value" },
      amountInCents: { type: "value" },
      notes: { type: "value" },
    },
  },
};

function mapSettlementStatementToForm(
  settlementStatement: LotAcqLotAcqSettlementStatementFragment,
): LotAcqSettlementstatementFormInput {
  return {
    id: settlementStatement.id,
    lotId: settlementStatement.lot.id,
    documentId: settlementStatement.document.id,
    buyerName: settlementStatement.buyerName,
    settlementDate: settlementStatement.settlementDate
      ? new Date(settlementStatement.settlementDate.toISOString())
      : undefined,
    lineItems: mapLotPartitionsToForm(settlementStatement.lineItems),
  };
}

export function mapLotPartitionsToForm(lineItems: Maybe<LotAcqLotAcqSettlementStatementLineItemFragment[]>) {
  const lines = lineItems?.map((li) => ({
    id: li.id,
    amountInCents: li.amountInCents,
    type: li.type.code,
    notes: li.notes,
  }));

  return lines ?? [];
}

export function getDynamicPdfVierwerWidth() {
  const { innerHeight } = window;
  return innerHeight > 900 ? 820 : innerHeight > 800 ? 760 : 640;
}

type HeaderRow = { kind: "header" };
type LineItemRow = {
  kind: "lineItem";
  id: string;
  data: ObjectState<{
    id: InputMaybe<string>;
    type: InputMaybe<LotAcqSettlementStatementLineItemType>;
    amountInCents: InputMaybe<number>;
    notes: InputMaybe<string>;
  }>;
};
type Row = HeaderRow | LineItemRow;
